"use client";

import React, {type ForwardedRef, useEffect, useRef, useState} from "react";
import Tippy from "@tippyjs/react/headless";
import "tippy.js/dist/tippy.css";

import {useTippyRegistry} from "../";
import {cn, useMergeRefs} from "@isudio/utils";

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  format?: "numeric" | "alphanumeric";
  error?: string;
  showToast?: (message: string) => void;
  prevFieldRef?: React.RefObject<HTMLElement>;
  nextFieldRef?: React.RefObject<HTMLElement>;
};

const OtpInput = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      maxLength = 6,
      format,
      error,
      onChange,
      onBlur,
      showToast,
      value,
      prevFieldRef,
      nextFieldRef,
      ...props
    },
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const { register, unregister } = useTippyRegistry();

    const mask =
      format === "numeric"
        ? /^[0-9]$/
        : format === "alphanumeric"
          ? /^[a-zA-Z0-9]$/
          : undefined;

    const containerRef = useRef<HTMLDivElement | null>(null);
    const inputsRef = useRef<HTMLInputElement[]>([]);
    const hiddenInputRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMergeRefs(ref, hiddenInputRef);

    useEffect(() => {
      if (!hiddenInputRef.current) return;

      (hiddenInputRef.current as any).focusFirst = () => {
        const first = inputsRef.current[0];
        if (first) {
          first.focus();
          requestAnimationFrame(() => {
            try { first.select(); } catch {}
          });
        }
      };

      return () => {
        try {
          delete (hiddenInputRef.current as any).focusFirst;
        } catch {}
      };
    }, []);

    const [values, setValues] = useState<string[]>(
      Array.from({ length: maxLength }).map(() => "")
    );

    const [showError, setShowError] = useState(false);
    const [hovering, setHovering] = useState(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearHideTimer = () => {
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    };
    const startHideTimer = (delay = 2000) => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => {
        setShowError(false);
        hideTimerRef.current = null;
      }, delay);
    };

    useEffect(() => {
      if (error && !hovering) {
        setShowError(true);
        startHideTimer();
      } else if (!error) {
        setShowError(false);
        clearHideTimer();
      }
      return clearHideTimer;
    }, [error, hovering]);

    useEffect(() => {
      if (value === undefined || value === null) return;
      try {
        const v = String(value);
        const arr = v.split("").slice(0, maxLength);
        const padded = arr.concat(Array.from({ length: maxLength - arr.length }).map(() => ""));
        setValues(padded);
      } catch {
      }
    }, [value, maxLength]);

    useEffect(() => {
      if (hiddenInputRef.current) hiddenInputRef.current.value = values.join("");
    }, [values]);

    useEffect(() => {
      const selectFirstIfFocused = () => {
        const first = inputsRef.current[0];
        if (first && first.value && document.activeElement === first) {
          requestAnimationFrame(() => {
            try { first.select(); } catch {}
          });
        }
      };

      selectFirstIfFocused();

      const container = containerRef.current;
      const onFocusIn = (ev: FocusEvent) => {
        const target = ev.target as HTMLElement | null;
        if (target && target === inputsRef.current[0] && inputsRef.current[0]?.value) {
          requestAnimationFrame(() => {
            try { inputsRef.current[0]?.select(); } catch {}
          });
        }
      };

      container?.addEventListener("focusin", onFocusIn);
      return () => container?.removeEventListener("focusin", onFocusIn);
    }, [values]);

    const focusInput = (index: number) => {
      const el = inputsRef.current[index];
      if (el) {
        el.focus();
        requestAnimationFrame(() => {
          try {
            el.select();
          } catch {
          }
        });
      }
    };

    const tryFocusRef = (r?: React.RefObject<HTMLElement> | null): boolean => {
      try {
        const el = r?.current;
        if (el && typeof el.focus === "function") {
          el.focus();
          return true;
        }
      } catch {
      }
      return false;
    };

    const focusOutsideFallback = (direction: "next" | "prev") => {
      const container = containerRef.current;
      if (!container) return;

      const form = container.closest("form");
      const root: Document | Element = form ?? document;

      const selector =
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const nodeList = (root as Document | Element).querySelectorAll<HTMLElement>(selector);
      const focusables = Array.from(nodeList).filter((el) => {
        const style = window.getComputedStyle(el);
        return el.offsetWidth > 0 || el.offsetHeight > 0 || style.visibility !== "hidden";
      });

      if (focusables.length === 0) return;

      const insideIndices: number[] = [];
      for (let i = 0; i < focusables.length; i++) {
        const el: any = focusables[i];
        if (container.contains(el)) insideIndices.push(i);
      }
      if (insideIndices.length === 0) return;

      const firstInside = insideIndices[0];
      const lastInside = insideIndices[insideIndices.length - 1];

      if (direction === "next") {
        const nextOutside = lastInside ? focusables[lastInside + 1] : undefined;
        if (nextOutside) nextOutside.focus();
      } else {
        const prevOutside = firstInside ? focusables[firstInside - 1] : undefined;
        if (prevOutside) prevOutside.focus();
      }
    };

    const handleBeforeInput = (e: InputEvent) => {
      if (mask && e.data && !mask.test(e.data)) {
        e.preventDefault();
        showToast?.("Invalid characters are blocked from input!");
      }
    };

    const emitParentChange = () => {
      if (!hiddenInputRef.current) return;
      const event = new Event("input", { bubbles: true });
      Object.defineProperty(event, "target", {
        writable: false,
        value: hiddenInputRef.current,
      });
      onChange?.(event as unknown as React.ChangeEvent<HTMLInputElement>);
    };

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      index: number
    ) => {
      const val = e.target.value.slice(-1);
      if (mask && val && !mask.test(val)) {
        showToast?.("Invalid character!");
        return;
      }

      const next = [...values];
      next[index] = val;
      setValues(next);
      if (hiddenInputRef.current) hiddenInputRef.current.value = next.join("");
      emitParentChange();
    };

    const handleInput = (
      _e: React.FormEvent<HTMLInputElement>,
      index: number
    ) => {
      const t = setTimeout(() => {
        if (index < maxLength - 1) {
          focusInput(index + 1);
        } else {
          const used = tryFocusRef(nextFieldRef);
          if (!used) focusOutsideFallback("next");
        }
        clearTimeout(t);
      }, 10)
    };

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
      index: number
    ) => {
      props.onKeyDown?.(e);

      if (e.key === "Backspace") {
        e.stopPropagation();
        e.preventDefault();
        const next = [...values];
        if (next[index]) {
          next[index] = "";
          setValues(next);
          if (hiddenInputRef.current) hiddenInputRef.current.value = next.join("");
          emitParentChange();
          focusInput(index);
        } else if (index > 0) {
          const prevIndex = index - 1;
          next[prevIndex] = "";
          setValues(next);
          if (hiddenInputRef.current) hiddenInputRef.current.value = next.join("");
          emitParentChange();
          focusInput(prevIndex);
        }
        return;
      }

      if (e.key === "ArrowLeft") {
        e.preventDefault();
        e.stopPropagation();
        if (index > 0) focusInput(index - 1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        if (index < maxLength - 1) focusInput(index + 1);
        return;
      }

      if (e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();

        if (e.shiftKey) {
          if (index > 0) {
            focusInput(index - 1);
          } else {
            const used = tryFocusRef(prevFieldRef);
            if (!used) focusOutsideFallback("prev");
          }
        } else {
          if (index < maxLength - 1) {
            focusInput(index + 1);
          } else {
            const used = tryFocusRef(nextFieldRef);
            if (!used) focusOutsideFallback("next");
          }
        }
        return;
      }
    };

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const onFocusOut = (ev: FocusEvent) => {
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (!container.contains(active)) {
            onBlur?.(ev as unknown as React.FocusEvent<HTMLInputElement>);
            if (error) {
              setShowError(true);
              startHideTimer();
            }
          }
        });
      };

      container.addEventListener("focusout", onFocusOut);
      return () => container.removeEventListener("focusout", onFocusOut);
    }, [onBlur, error]);

    const handleMouseEnter = () => {
      if (!error) return;
      clearHideTimer();
      setHovering(true);
      setShowError(true);
    };

    const handleMouseLeave = () => {
      setHovering(false);
      setShowError(false);
      clearHideTimer();
    };

    return (
      <Tippy
        onCreate={(instance) => register(instance)}
        onDestroy={(instance) => unregister(instance)}
        className="err-tooltip"
        placement="bottom"
        visible={!!error && showError}
        interactive={false}
        hideOnClick={true}
        delay={0}
        render={(attrs) => (
          <div
            {...attrs}
            tabIndex={-1}
            className="err-tooltip-box box bg-[#640a0a] text-white text-sm px-2 py-1 rounded shadow whitespace-pre-line"
          >
            {error ?? ""}
            <div
              data-popper-arrow
              className="err-tooltip-arrow bg-[#640a0a]! w-3! h-3! rotate-45!"
            />
          </div>
        )}
      >
        <div
          ref={containerRef}
          className="flex gap-2"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* hidden input for integration with form libs */}
          <input
            ref={mergedRef}
            type="text"
            hidden
            readOnly
            value={values.join("")}
            onChange={() => {}}
          />

          {Array.from({ length: maxLength }).map((_, i) => (
            <input
              key={i}
              type={type}
              inputMode={format === "numeric" ? "numeric" : "text"}
              value={values[i]}
              ref={(el) => {
                inputsRef.current[i] = el!;
              }}
              maxLength={1}
              onBeforeInput={(e) => handleBeforeInput(e.nativeEvent as InputEvent)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onChange={(e) => handleChange(e, i)}
              onInput={(e) => handleInput(e, i)}
              onFocus={() => {
                const el = inputsRef.current[i];
                requestAnimationFrame(() => el?.select());
              }}
              className={cn(
                "flex h-9 w-9 border border-input-field bg-transparent px-3 py-1 text-center text-base focus-visible:outline-none focus-visible:ring-0 placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                props.readOnly ? "text-generic-dull" : "",
                error ? "border-input-field__error" : "",
                className
              )}
              {...props}
            />
          ))}
        </div>
      </Tippy>
    );
  }
);

OtpInput.displayName = "OtpInput";
export {OtpInput};
